// EntitesMobile.jsx — onglets « Clients » et « Fournisseurs » en version mobile.
// Un seul écran paramétré par `entityType`, comme EntityDetailsModal.jsx:25-36 : les
// deux composants desktop sont des quasi-copies. Différences réelles : libellé du total,
// section Fidélité (clients seulement), défaut de `opening_balance_type`.
//
// CRUD complet, sauf l'upload de logo qui reste sur ordinateur.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Fab, Sheet,
  ActionRow, DetailRow, Note, Field, TextInput, TextAreaInput, SelectInput,
  Pill, Mono, Ico, fmt, fmtMoney, useMobileMenu,
} from "../produits/productMobileKit";
import { useAuth } from "../../context/AuthContext";
import useBusinessMutationInvalidation from "../../hooks/useBusinessMutationInvalidation";
import { useSelection } from "../../SelectionContext";
import { normalizeStatus } from "../../gestion-entites/Gestion.entitie_client";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { fetchEntities, fetchWarehouses, saveEntity } from "./entitesMobileApi";

const CONFIG = {
  customer: {
    title: "Clients", singular: "client", fabLabel: "Nouveau client",
    totalLabel: "Total ventes", userType: "client",
    defaultBalanceType: "receive", hasLoyalty: true,
    perm: "Gestion Commerciale.Entites.Clients",
    otherTab: { label: "Fournisseurs", path: "/gestion-entites/fournisseurs", perm: "Gestion Commerciale.Entites.Fournisseurs.view" },
  },
  supplier: {
    title: "Fournisseurs", singular: "fournisseur", fabLabel: "Nouveau fournisseur",
    totalLabel: "Total achats", userType: "fournisseur",
    defaultBalanceType: "pay", hasLoyalty: false,
    perm: "Gestion Commerciale.Entites.Fournisseurs",
    otherTab: { label: "Clients", path: "/gestion-entites/clients", perm: "Gestion Commerciale.Entites.Clients.view" },
  },
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const idOf = (v) => (v && typeof v === "object" ? v.id : v);
const isActive = (e) => normalizeStatus(e.status) === "enabled";

const emptyForm = (cfg, warehouseId) => ({
  name: "", email: "", phone: "", status: "enabled",
  address: "", shipping_address: "",
  warehouse_id: warehouseId ? String(warehouseId) : "",
  user_details: {
    rccm: "", ifu: "", credit_period: "30", credit_limit: "0",
    opening_balance: "0", opening_balance_type: cfg.defaultBalanceType,
    loyalty_status: "active", loyalty_status_reason: "",
  },
});

export default function EntitesMobile({ entityType = "customer", onMenu: onMenuProp }) {
  const cfg = CONFIG[entityType];
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const invalidateEntities = useBusinessMutationInvalidation(entityType);
  const { selectedCompany, selectedWarehouse } = useSelection();

  const companyId = idOf(selectedCompany);
  const warehouseId = idOf(selectedWarehouse);

  const can = {
    create: hasPermission(`${cfg.perm}.create`),
    edit: hasPermission(`${cfg.perm}.edit`),
    delete: hasPermission(`${cfg.perm}.delete`),
  };

  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const [navOpen, setNavOpen] = useState(false);
  const [sheet, setSheet] = useState(null);        // {mode:'detail'|'form'|'actions', entity}
  const [form, setForm] = useState(() => emptyForm(cfg, warehouseId));
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) { setRows([]); return; }
    setLoading(true);
    try {
      setRows(await fetchEntities(entityType, { companyId, warehouseId }));
    } catch {
      message.error(`Impossible de récupérer les ${cfg.title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [entityType, companyId, warehouseId, cfg.title]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!companyId) { setWarehouses([]); return; }
    let alive = true;
    fetchWarehouses(companyId)
      .then((w) => alive && setWarehouses(w))
      .catch(() => alive && setWarehouses([]));
    return () => { alive = false; };
  }, [companyId]);

  const activeCount = useMemo(() => rows.filter(isActive).length, [rows]);

  // Filtrage client, comme le desktop : la liste est chargée intégralement.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((e) => {
      if (filter === "active" && !isActive(e)) return false;
      if (filter === "inactive" && isActive(e)) return false;
      if (q && ![e.name, e.email, e.phone].some((v) => String(v || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, query, filter]);

  const warehouseOptions = useMemo(
    () => warehouses.map((w) => ({ value: String(w.id), label: w.name })),
    [warehouses]
  );

  /* ---------- ouverture des feuilles ---------- */
  const openCreate = () => {
    if (!companyId) return message.warning("Veuillez sélectionner une entreprise");
    setForm(emptyForm(cfg, warehouseId));
    setSheet({ mode: "form", entity: null });
  };

  const openEdit = (e) => {
    setForm({
      name: e.name || "", email: e.email || "", phone: e.phone || "",
      status: normalizeStatus(e.status),
      address: e.address || "", shipping_address: e.shipping_address || "",
      warehouse_id: e.warehouse_id ? String(e.warehouse_id) : (warehouseId ? String(warehouseId) : ""),
      user_details: {
        rccm: e.rccm || "", ifu: e.ifu || "",
        credit_period: String(e.credit_period ?? "30"),
        credit_limit: String(e.credit_limit ?? "0"),
        opening_balance: String(e.opening_balance ?? "0"),
        opening_balance_type: e.opening_balance_type || cfg.defaultBalanceType,
        loyalty_status: e.loyalty_status || "active",
        loyalty_status_reason: e.loyalty_status_reason || "",
      },
    });
    setSheet({ mode: "form", entity: e });
  };

  const close = () => { if (!saving) setSheet(null); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDetail = (k, v) => setForm((f) => ({ ...f, user_details: { ...f.user_details, [k]: v } }));

  const formValid =
    form.name.trim().length >= 2 &&
    form.address.trim().length > 0 &&
    Boolean(form.warehouse_id);

  const submit = async () => {
    if (!formValid) return message.warning("Nom, adresse et établissement sont obligatoires");
    setSaving(true);
    try {
      await saveEntity(entityType, sheet.entity?.id, form, { companyId });
      void invalidateEntities();
      message.success(sheet.entity ? "Modifications enregistrées" : `${cfg.singular} créé avec succès`);
      setSheet(null);
      load();
    } catch (err) {
      message.error(
        "Erreur lors de l'enregistrement : " +
        (err.response?.data?.error || err.message)
      );
    } finally { setSaving(false); }
  };

  const balanceLabel = form.user_details.opening_balance_type === "pay" ? "à payer" : "à recevoir";

  /* ---------- rendu ---------- */
  if (!companyId) {
    return (
      <ProductScreen>
        <Appbar title={cfg.title} onMenu={onMenu} />
        <div className="body">
          <EmptyState icon="store" title="Aucune entreprise sélectionnée" text={`Choisissez une entreprise pour voir vos ${cfg.title.toLowerCase()}.`} />
        </div>
      </ProductScreen>
    );
  }

  const detailEntity = sheet?.mode === "detail" ? sheet.entity : null;

  return (
    <div className="clients-mobile-workspace">
    <ProductScreen>
      <Appbar
        title={cfg.title} onMenu={onMenu}
        subtitle={`${fmt(rows.length)} ${rows.length > 1 ? cfg.title.toLowerCase() : cfg.singular}`}
        action={
          hasPermission(cfg.otherTab.perm) ? (
            <button className="iconbtn" aria-label="Aller à l'autre onglet" onClick={() => setNavOpen(true)}>
              <Ico name="list-ordered" />
            </button>
          ) : null
        }
      />

      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <Summary items={[
          { label: cfg.title, value: fmt(rows.length) },
          { label: "Actifs", value: fmt(activeCount) },
          { label: "Désactivés", value: fmt(rows.length - activeCount) },
        ]} />
        <SearchBar value={query} onChange={setQuery} placeholder={`Rechercher un ${cfg.singular}…`} />
        <Chips value={filter} onChange={setFilter} options={[
          { key: "all", label: "Tous" }, { key: "active", label: "Actifs" }, { key: "inactive", label: "Désactivés" },
        ]} />
      </div>

      <div className="body">
        {loading && !rows.length ? (
          <EmptyState loading title="Chargement…" text="Synchronisation en cours." />
        ) : !visible.length ? (
          <EmptyState
            icon="tag"
            title={query || filter !== "all" ? "Aucun résultat" : `Aucun ${cfg.singular}`}
            text={query || filter !== "all"
              ? "Aucune fiche ne correspond à votre recherche."
              : `Créez votre premier ${cfg.singular}.`}
          />
        ) : (
          <div className="list">
            {visible.map((e) => {
              const due = num(e.financial_due_amount);
              return (
                <button key={e.id} className="rcard" onClick={() => setSheet({ mode: "detail", entity: e })}>
                  <Mono name={e.name} />
                  <div className="rcard__main">
                    <div className="rcard__name">{e.name}</div>
                    <div className="rcard__meta">
                      <Pill tone={isActive(e) ? "ok" : "off"} label={isActive(e) ? "Activé" : "Désactivé"} />
                    </div>
                    {(e.phone || e.warehouse_name) ? (
                      <div className="rcard__meta">
                        <span className="rcard__sub">
                          {[e.phone, e.warehouse_name].filter(Boolean).join(" · ")}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <span className="rcard__price">
                    {fmtMoney(e.financial_total_amount)}
                    {due > 0 ? <small style={{ color: "var(--red)" }}> dû {fmtMoney(due)}</small> : null}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {can.create ? <Fab onClick={openCreate} label={cfg.fabLabel} /> : null}

      {/* feuille : navigation vers l'autre onglet */}
      <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Aller à…">
        <ActionRow
          icon="chevron-right" label={cfg.otherTab.label}
          onClick={() => { setNavOpen(false); navigate(cfg.otherTab.path); }}
        />
      </Sheet>

      {/* feuille : détail */}
      <Sheet
        open={Boolean(detailEntity)} onClose={close}
        title={detailEntity?.name} subtitle={detailEntity?.email || detailEntity?.phone}
      >
        {detailEntity ? (
          <>
            <DetailRow label={cfg.totalLabel} value={fmtMoney(detailEntity.financial_total_amount)} />
            <DetailRow label="Montant dû" value={fmtMoney(detailEntity.financial_due_amount)} />
            <DetailRow label="Factures impayées" value={fmt(detailEntity.financial_unpaid_count)} />
            <DetailRow label="Limite de crédit" value={fmtMoney(detailEntity.credit_limit)} />

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Coordonnées</div>
            <DetailRow label="Statut" value={isActive(detailEntity) ? "Activé" : "Désactivé"} />
            <DetailRow label="Téléphone" value={detailEntity.phone || "—"} />
            <DetailRow label="Email" value={detailEntity.email || "—"} />
            <DetailRow label="Établissement" value={detailEntity.warehouse_name || "—"} />
            <DetailRow label="Adresse" value={detailEntity.address || "—"} />
            {detailEntity.shipping_address ? (
              <DetailRow label="Adresse de livraison" value={detailEntity.shipping_address} />
            ) : null}

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Informations commerciales</div>
            <DetailRow label="RCCM" value={detailEntity.rccm || "—"} />
            <DetailRow label="IFU" value={detailEntity.ifu || "—"} />
            <DetailRow label="Période de crédit" value={`${fmt(detailEntity.credit_period)} j`} />
            <DetailRow
              label={`Solde d'ouverture (${detailEntity.opening_balance_type === "pay" ? "à payer" : "à recevoir"})`}
              value={fmtMoney(detailEntity.opening_balance)}
            />
            {cfg.hasLoyalty ? (
              <DetailRow label="Fidélité" value={detailEntity.loyalty_status || "—"} />
            ) : null}

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Traçabilité</div>
            <DetailRow label="Créé par" value={detailEntity.created_by_name || "—"} />
            <DetailRow label="Modifié par" value={detailEntity.last_modified_by_name || "—"} />

            <div style={{ marginTop: 16 }}>
              {can.edit ? <ActionRow icon="pencil" label="Modifier" onClick={() => openEdit(detailEntity)} /> : null}
              {can.delete ? (
                <ActionRow icon="trash-2" label="Supprimer" danger onClick={() => { setSheet(null); setToDelete(detailEntity); }} />
              ) : null}
            </div>
          </>
        ) : null}
      </Sheet>

      {/* feuille : formulaire */}
      <Sheet
        open={sheet?.mode === "form"} onClose={close}
        title={sheet?.entity ? `Modifier le ${cfg.singular}` : cfg.fabLabel}
        subtitle="Les champs marqués d'une étoile sont obligatoires."
      >
        <div className="field__lbl" style={{ margin: "4px 0 8px" }}>Informations générales</div>
        <Field label="Nom ou raison sociale *">
          <TextInput value={form.name} onChange={(v) => setField("name", v)} placeholder="Ex : SARL Exemple" icon="tag" />
        </Field>
        <Field label="Email">
          <TextInput value={form.email} onChange={(v) => setField("email", v)} placeholder="contact@exemple.com" inputMode="email" />
        </Field>
        <Field label="Téléphone">
          <TextInput value={form.phone} onChange={(v) => setField("phone", v)} placeholder="70 00 00 00" inputMode="tel" />
        </Field>
        <Field label="Statut *">
          <SelectInput
            value={form.status} onChange={(v) => setField("status", v || "enabled")}
            options={[{ value: "enabled", label: "Activé" }, { value: "disabled", label: "Désactivé" }]}
          />
        </Field>

        <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Adresse</div>
        <Field label="Adresse *">
          <TextAreaInput value={form.address} onChange={(v) => setField("address", v)} placeholder="Adresse complète…" rows={2} />
        </Field>
        <Field label="Adresse de livraison" hint="facultatif">
          <TextAreaInput value={form.shipping_address} onChange={(v) => setField("shipping_address", v)} placeholder="Si différente…" rows={2} />
        </Field>

        <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Association</div>
        <Field label="Établissement *">
          <SelectInput
            value={form.warehouse_id} onChange={(v) => setField("warehouse_id", v || "")}
            options={warehouseOptions} placeholder="Sélectionner un établissement"
          />
        </Field>

        <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Informations commerciales</div>
        <Field label="RCCM"><TextInput value={form.user_details.rccm} onChange={(v) => setDetail("rccm", v)} /></Field>
        <Field label="IFU"><TextInput value={form.user_details.ifu} onChange={(v) => setDetail("ifu", v)} /></Field>

        <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Conditions de crédit</div>
        <Field label="Période de crédit (jours) *">
          <TextInput value={form.user_details.credit_period} onChange={(v) => setDetail("credit_period", v)} inputMode="numeric" />
        </Field>
        <Field label="Limite de crédit *">
          <TextInput value={form.user_details.credit_limit} onChange={(v) => setDetail("credit_limit", v)} inputMode="decimal" />
        </Field>

        <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Solde initial</div>
        <Field label={`Montant * (${balanceLabel})`}>
          <TextInput value={form.user_details.opening_balance} onChange={(v) => setDetail("opening_balance", v)} inputMode="decimal" />
        </Field>
        <Field label="Sens du solde">
          <SelectInput
            value={form.user_details.opening_balance_type}
            onChange={(v) => setDetail("opening_balance_type", v || cfg.defaultBalanceType)}
            options={[{ value: "receive", label: "À recevoir" }, { value: "pay", label: "À payer" }]}
          />
        </Field>

        {cfg.hasLoyalty ? (
          <>
            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Programme de fidélité</div>
            <Field label="Statut de fidélité">
              <SelectInput
                value={form.user_details.loyalty_status}
                onChange={(v) => setDetail("loyalty_status", v || "active")}
                options={[
                  { value: "active", label: "Active" },
                  { value: "disabled", label: "Désactivée" },
                  { value: "revoked", label: "Révoquée" },
                ]}
              />
            </Field>
            {form.user_details.loyalty_status !== "active" ? (
              <Field label="Motif">
                <TextAreaInput
                  value={form.user_details.loyalty_status_reason}
                  onChange={(v) => setDetail("loyalty_status_reason", v)}
                  placeholder="Motif de la désactivation…" rows={2}
                />
              </Field>
            ) : null}
          </>
        ) : null}

        <div style={{ marginTop: 12 }}>
          <Note>Le logo se modifie sur ordinateur ; il est préservé lors d'une modification depuis le mobile.</Note>
        </div>

        <div className="sheet__actions">
          <button className="btn btn--ghost" onClick={close} disabled={saving}>Annuler</button>
          <button className="btn btn--primary" onClick={submit} disabled={saving || !formValid}>
            <Ico name={saving ? "loader-circle" : "check"} className={saving ? "spin" : ""} />
            {sheet?.entity ? "Mettre à jour" : "Créer"}
          </button>
        </div>
      </Sheet>

      {/* Réutilise le modal desktop : c'est LUI qui exécute la suppression (normale,
          forcée, archivage ou désactivation) après analyse des dépendances, puis
          rappelle onConfirm(action) — on se contente donc de rafraîchir la liste. */}
      <DeleteConfirmationModal
        visible={Boolean(toDelete)}
        onCancel={() => setToDelete(null)}
        onConfirm={(action) => {
          setToDelete(null);
          const done = { delete: "supprimé", archive: "archivé", disable: "désactivé" }[action];
          message.success(`${cfg.singular} ${done || "traité"} avec succès`);
          load();
        }}
        user={toDelete}
        userType={cfg.userType}
      />
    </ProductScreen>
    </div>
  );
}
